// ============================================================================
// PRELIT-ASGN-001 — Assign / Reassign Agent
// ============================================================================
// Small modal. For UNCLAIMED rows the agent is set without creating a workflow
// instance (case stays UNCLAIMED). For IN_PROGRESS cases this is a reassign —
// updates assigned_agent_user_id without changing workflow state.

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import { SyutSelect } from 'components/ui';
import notificationService from 'services/notificationService';
import { useAssignAgent } from 'hooks/api/usePreLitigation';
import { preLitigationService } from 'api/services/preLitigationService';
import type { ID } from 'api/types';

interface AssignAgentModalProps {
  isOpen: boolean;
  caseId: string;
  caseCode: string;
  /** Branch the case is associated with — drives the footprint-based agent lookup. */
  branchId?: ID | null;
  /** Currently assigned agent (for "currently assigned to..." display). */
  currentAgentName?: string | null;
  /** Optional role to filter the dropdown by. Defaults to recsuit_field_agent.
   *  Pass 'recsuit_branch_head' / 'recsuit_legal_lead' / etc. when reassigning at higher tiers. */
  roleCode?: string;
  /** Optional override — if the caller already has a list, pass it and skip the API call. */
  agentOptions?: { value: string; label: string }[];
  onClose: () => void;
  onAssigned: () => void;
}

interface FormState {
  agentUserId: string;
  note: string;
}

const INITIAL: FormState = { agentUserId: '', note: '' };

const AssignAgentModal: React.FC<AssignAgentModalProps> = ({
  isOpen, caseId, caseCode, branchId, currentAgentName, roleCode = 'recsuit_field_agent', agentOptions, onClose, onAssigned,
}) => {
  const [form, setForm] = useState<FormState>(INITIAL);
  const formRef = useRef(form);
  formRef.current = form;
  const snapRef = useRef('');
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  const assign = useAssignAgent();

  // ---- Agent dropdown — load by footprint if no override passed ----
  const [loadedAgentOptions, setLoadedAgentOptions] = useState<{ value: string; label: string }[]>([]);
  const [loadingAgents, setLoadingAgents] = useState(false);
  const [agentLoadError, setAgentLoadError] = useState<string | null>(null);

  const effectiveOptions = agentOptions && agentOptions.length > 0
    ? agentOptions
    : loadedAgentOptions;

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!isOpen) return;
    setForm({ ...INITIAL });
    snapRef.current = JSON.stringify(INITIAL);
    setFieldErrors({});
    setAgentLoadError(null);

    // Skip the API call when caller passes their own options.
    if (agentOptions && agentOptions.length > 0) return;

    if (!branchId) {
      setAgentLoadError('Case has no branch assigned — cannot resolve eligible agents.');
      setLoadedAgentOptions([]);
      return;
    }

    let cancelled = false;
    setLoadingAgents(true);
    preLitigationService
      .getAgentsByFootprint(branchId, roleCode)
      .then((res) => {
        if (cancelled) return;
        const list = (res?.data ?? []).map((u: any) => ({
          value: String(u.userId),
          label: u.displayName + (u.email ? ` · ${u.email}` : ''),
        }));
        setLoadedAgentOptions(list);
        if (list.length === 0) {
          setAgentLoadError(
            `No active users with role ${roleCode} cover this branch. Set up team assignments via Team Setup (Phase 8) or seed admin.tbl_user_role / tbl_user_branch directly.`,
          );
        }
      })
      .catch((err: any) => {
        if (cancelled) return;
        setLoadedAgentOptions([]);
        setAgentLoadError(err?.response?.data?.message ?? 'Failed to load agents.');
      })
      .finally(() => { if (!cancelled) setLoadingAgents(false); });

    return () => { cancelled = true; };
  }, [isOpen, branchId, roleCode]);

  const isDirty = useMemo(() => {
    if (!snapRef.current) return false;
    return JSON.stringify(form) !== snapRef.current;
  }, [form]);

  const handleClose = useCallback(() => {
    if (assign.loading) return;
    if (isDirty) setDiscardConfirmOpen(true); else onClose();
  }, [isDirty, assign.loading, onClose]);

  const handleSave = useCallback(() => {
    const errors: Record<string, string> = {};
    if (!form.agentUserId) errors.agentUserId = 'Pick an agent.';
    if (Object.keys(errors).length) { setFieldErrors(errors); return; }
    setFieldErrors({});
    setSaveConfirmOpen(true);
  }, [form]);

  const handleConfirmSave = useCallback(async () => {
    setSaveConfirmOpen(false);
    try {
      await assign.run(caseId, { agentUserId: form.agentUserId, note: form.note || undefined });
      notificationService.toast.success(`Case ${caseCode} assigned.`);
      onAssigned();
      onClose();
    } catch (err: any) {
      const msg = err?.response?.data?.message ?? 'Failed to assign. Please try again.';
      notificationService.toast.error(msg, 6000);
    }
  }, [assign, caseId, caseCode, form, onAssigned, onClose]);

  if (!isOpen) return null;

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-sm">
        {assign.loading && <LoadingOverlay text="Assigning..." />}

        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Assign / Reassign Agent</h2>
          <button className="modal-close" onClick={handleClose} disabled={assign.loading}>
            <IconSystem.x size={20} />
          </button>
        </div>

        <div className="modal-content">
          <div style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', marginBottom: '0.75rem' }}>
            Case <strong>{caseCode}</strong>
            {currentAgentName ? <> · currently assigned to <strong>{currentAgentName}</strong></> : ' · currently unassigned'}
          </div>

          <div className="form-group" data-field="agentUserId">
            <SyutSelect
              label={<>Agent <span className="text-danger">*</span></>}
              options={effectiveOptions}
              value={form.agentUserId}
              onChange={(v) => {
                setFieldErrors(p => { const n = { ...p }; delete n.agentUserId; return n; });
                setForm(p => ({ ...p, agentUserId: (v as string) || '' }));
              }}
              error={fieldErrors.agentUserId}
              placeholder={loadingAgents ? 'Loading agents…' : 'Pick an agent...'}
              isDisabled={loadingAgents}
            />
            {agentLoadError && (
              <div style={{
                fontSize: '0.7rem',
                color: 'var(--text-secondary)',
                marginTop: '0.4rem',
                padding: '0.4rem 0.6rem',
                background: 'var(--bg-secondary)',
                border: '1px solid var(--border-color)',
                borderRadius: '4px',
                lineHeight: 1.4,
              }}>
                {agentLoadError}
              </div>
            )}
          </div>

          <div className="form-group">
            <label className="form-label">Note <span style={{ color: 'var(--text-muted)' }}>(optional)</span></label>
            <textarea
              className="form-textarea"
              rows={3}
              maxLength={500}
              value={form.note}
              onChange={(e) => setForm(p => ({ ...p, note: e.target.value }))}
              placeholder="Why are you reassigning?"
            />
          </div>
        </div>

        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={assign.loading}>Cancel</button>
          <button className="btn btn-primary" onClick={handleSave} disabled={assign.loading}>Assign</button>
        </div>
      </div>

      <ConfirmDialog
        open={saveConfirmOpen}
        title="Confirm Assignment"
        message={`Assign case ${caseCode} to the selected agent?`}
        confirmLabel="Assign"
        cancelLabel="Cancel"
        variant="default"
        onConfirm={handleConfirmSave}
        onCancel={() => setSaveConfirmOpen(false)}
      />
      <ConfirmDialog
        open={discardConfirmOpen}
        title="Discard Changes?"
        message="You have unsaved changes. Discard?"
        confirmLabel="Discard"
        cancelLabel="Keep Editing"
        variant="danger"
        onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }}
        onCancel={() => setDiscardConfirmOpen(false)}
      />
    </>
  );
};

const LoadingOverlay: React.FC<{ text: string }> = ({ text }) => (
  <div style={{
    position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)',
    display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
    zIndex: 10, borderRadius: 'inherit', gap: '0.75rem',
  }}>
    <div style={{
      width: '2rem', height: '2rem', border: '3px solid var(--border-color)',
      borderTopColor: '#2563eb', borderRadius: '50%', animation: 'spin 0.8s linear infinite',
    }} />
    <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{text}</span>
  </div>
);

export default AssignAgentModal;
